/**
 * Modal de boas-vindas do primeiro acesso.
 *
 * Aparece quando o usuario nunca interagiu com o onboarding (nenhum tour
 * concluido, nada dispensado). "Agora não" fecha so o modal (indicadores da
 * sidebar continuam ate cada modulo ser percorrido); "Não mostrar dicas"
 * esconde o modal E todos os indicadores. Fechar pelo X/backdrop/Esc tem o
 * mesmo efeito de "Agora não" — evita reaparecer a cada carregamento por um
 * fechamento acidental.
 */
import * as React from 'react';
import { Sparkles, X } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-store';
import { useTour } from '@/lib/tour/tour-provider';
import { cn } from '@/lib/utils';

export function WelcomeModal() {
  const status = useAuthStore((s) => s.status);
  const onboarding = useAuthStore((s) => s.onboarding);
  const setOnboarding = useAuthStore((s) => s.setOnboarding);
  const { requestTour } = useTour();

  // Fechamento otimista: o modal some na hora, sem esperar o PATCH. Se o
  // PATCH falhar, o estado do servidor nao muda — mas isso so importa no
  // proximo login (novo bootstrap de /auth/me); nesta sessao o modal nao
  // volta a aparecer por cima do tour, ja que `locallyClosed` nao depende
  // da resposta da rede.
  const [locallyClosed, setLocallyClosed] = React.useState(false);

  const patchMutation = useMutation({
    mutationFn: (patch: { dismissed?: boolean; dismissedAll?: boolean }) =>
      apiClient.meUpdateOnboarding(patch),
    onSuccess: (next) => setOnboarding(next),
    onError: () => {
      // Silencioso de proposito: o fechamento ja aconteceu (otimista) e nao
      // ha nada de destrutivo pra reverter. Pior caso: o dismissed nao
      // persistiu e o modal volta no proximo login.
    },
  });

  const open =
    status === 'authenticated' &&
    !locallyClosed &&
    !onboarding.dismissed &&
    !onboarding.dismissedAll &&
    onboarding.completed.length === 0;

  function close() {
    setLocallyClosed(true);
    patchMutation.mutate({ dismissed: true });
  }

  function startTour() {
    setLocallyClosed(true);
    patchMutation.mutate({ dismissed: true });
    requestTour('dashboard');
  }

  function hideAll() {
    setLocallyClosed(true);
    patchMutation.mutate({ dismissedAll: true });
  }

  React.useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[75] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-modal-title"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={close}
        aria-hidden
      />
      <div
        className={cn(
          'relative z-[76] w-full max-w-md overflow-hidden rounded-lg border shadow-xl',
          'border-[var(--modal-border,var(--card-border))] bg-[var(--modal-bg,var(--card-bg))] backdrop-blur-[var(--blur)]',
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-[var(--border)] px-5 py-4">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/15 text-[var(--accent)]">
              <Sparkles className="h-4 w-4" />
            </span>
            <h2 id="welcome-modal-title" className="text-base font-semibold text-[var(--fg)]">
              Bem-vindo ao VoxisAdv
            </h2>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Fechar"
            className="rounded-md p-1 text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="px-5 py-4">
          <p className="text-sm text-[var(--fg-muted)]">
            Preparamos um tour rápido pelo Dashboard para você conhecer os
            principais recursos. Os módulos com um ponto pulsante na barra
            lateral também têm um tour próprio, disponível a qualquer momento.
          </p>
        </div>
        <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--border)] bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] px-5 py-3">
          <button
            type="button"
            onClick={hideAll}
            className="text-xs font-medium text-[var(--fg-muted)] hover:text-[var(--fg)]"
          >
            Não mostrar dicas
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={close}
              className="rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-1.5 text-xs font-medium text-[var(--fg-muted)] hover:text-[var(--fg)]"
            >
              Agora não
            </button>
            <button
              type="button"
              onClick={startTour}
              className="rounded-md bg-[var(--accent)] px-3 py-1.5 text-xs font-medium text-[var(--accent-fg)] hover:opacity-90"
            >
              Começar tour
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
